/**
 * cameraRigCompat — the `window.__ORBIT_CONTROLS__` surface, backed by the camera rig
 * instead of by drei's OrbitControls (camera-in-blueprint).
 *
 * WHY THIS EXISTS. Replacing OrbitControls in live mode would silently break four existing
 * consumers that reach for the global. This object keeps every one of them working with no
 * change at their call sites:
 *
 *   1. aosTools/threejs/bake_ibl.mjs:174-180 sets `.enabled = false` so its per-face
 *      lookAt() isn't overwritten every frame. Without it "every face renders the same
 *      view" and the IBL bake is SILENTLY CORRUPT — bad assets, no error. So `.enabled`
 *      stands the rig down. This is the single most important line in the file.
 *   2. The OGS eye branch subscribes to `change` as its cheap re-colour path (appr is
 *      view-dependent) → EventDispatcher, dispatched whenever the rig moves. It is NOT the
 *      only path, by design: this global's identity swaps on a live↔edit flip, so the eyes
 *      also watch the camera per frame (syncCamera) and the head/multi-region recolor watches
 *      it exclusively (src/ogs/cameraWatch.ts) — a listener bound once at pipeline-ready
 *      landed on the drei OrbitControls this object then replaced, and the splat colour froze
 *      in live mode with nothing to show why.
 *   3. App.jsx:92-113 frameAvatar() (the F hotkey) writes camera.position + target, then
 *      calls update().
 *   4. StreamAvatarModel.jsx:243-297 saves/restores target/minDistance/maxDistance around a
 *      stream session and calls update(). It uses `.clone()`/`.copy()`, so `target` MUST be
 *      a real THREE.Vector3, not an array.
 *   ...plus Scene.jsx's CameraCageGizmo, which reads `.target` each frame.
 *
 * WHY NOT KEEP A REAL (input-disabled) OrbitControls AS THE SHIM? Not for the reason you'd
 * guess — OrbitControls.update() is idempotent against external writes (it re-derives its
 * spherical from the LIVE camera every call: `offset.copy(object.position).sub(target)`),
 * which is exactly why frameAvatar works today. The real reasons:
 *   - Clamp double-authority: a real OC enforces its OWN min/maxDistance + polar clamps every
 *     frame, so a rig pose blend transiting outside them gets snapped mid-blend. Here the rig
 *     limits are the single clamp authority and these fields map straight onto them.
 *   - Dead input machinery: a real OC left enabled (so the bake tool's toggle stays
 *     meaningful) keeps live DOM listeners and its own pointer capture on the same canvas as
 *     inputComponent — a second, half-dead input handler whose only job is to be ignored.
 *   - `.enabled` has to be honoured by the rig either way, so a real OC buys no contract free.
 *
 * update() is REVERSE-KINEMATICS: it adopts whatever the outside writer did (target here,
 * camera position via syncFromCamera) back into the rig. Same re-derivation a real
 * OrbitControls does every frame — that's what makes consumers 3 and 4 work untouched.
 */

import * as THREE from 'three'
import { cameraController } from './cameraController.js'
import { eventGraphState } from './eventGraphState.js'

// Reused, like OrbitControls' own _changeEvent — dispatch happens per frame while dragging,
// and EventDispatcher only stamps `.target` onto it.
const CHANGE_EVENT = { type: 'change' }

class CameraRigCompat extends THREE.EventDispatcher {
  constructor() {
    super()
    /** Look-at point. Written by the rig every frame; read (and written) by consumers. */
    this.target = new THREE.Vector3(0, 1.35, 0)
    /** false → the rig stands down entirely. The bake-tool contract. */
    this.enabled = true
  }

  /**
   * The camera being driven — OrbitControls' own `.object`, and the one field of the
   * contract this shim used to be missing. A consumer that reads `__ORBIT_CONTROLS__.object`
   * got a camera in edit mode (and in live's editor-camera fallback) and `undefined` the
   * moment a blueprint grew a camera rig — the identity swap described in the header, but
   * failing as a TypeError rather than as a stale listener. The rig already holds the live
   * camera, so serve it: `.position` writes here are adopted by update() exactly as they are
   * on the real controls. Null before a camera is mounted, never a fabricated one.
   */
  get object() { return cameraController._camera ?? null }

  // The rig limits ARE the distance clamp — no second authority.
  get minDistance() { return cameraController._limits.minDist }
  set minDistance(v) { cameraController.setRigLimits({ minDist: v }) }
  get maxDistance() { return cameraController._limits.maxDist }
  set maxDistance(v) { cameraController.setRigLimits({ maxDist: v }) }

  /**
   * Adopt an out-of-band camera/target write into the rig, then notify.
   * Callers set `.target` and/or `camera.position` and call this — exactly the OrbitControls
   * contract they already code against.
   */
  update() {
    cameraController.setRigTarget(this.target.x, this.target.y, this.target.z)
    cameraController.syncFromCamera()
    this.dispatchEvent(CHANGE_EVENT)
    return true
  }

  notifyChange() { this.dispatchEvent(CHANGE_EVENT) }
}

export const cameraRigCompat = new CameraRigCompat()

// Node types that mean "this blueprint drives the camera". Used for the no-rig report below.
const RIG_NODE_TYPES = new Set([
  'cameraAddYaw', 'cameraAddPitch', 'cameraAddDolly', 'cameraPanTarget',
  'cameraSetRigLimits', 'cameraSetRigPose', 'setCameraFollow',
])

/**
 * Does the live event graph actually drive the camera?
 *
 * The camera is now entirely authored: with no camera nodes in the blueprint, the viewport
 * genuinely doesn't respond — that's the design, not a bug. But an unexplained frozen camera
 * is indistinguishable from a broken build, so this is reported LOUDLY (a viewport banner,
 * see CameraRigNotice) rather than silently tolerated. Consistent with the repo's rule that
 * loud failure beats silent fakery: we don't quietly substitute a default camera.
 *
 * Note this is only ever a *live-mode* concern — edit mode keeps drei's OrbitControls, so
 * authoring is never bricked and a dead live camera is always recoverable from the editor.
 *
 * Scans one level into any `graphGroup` container's `data.innerNodes` (camera-in-blueprint
 * Phase 3) — `graphGroup` is deliberately NOT added to RIG_NODE_TYPES itself, so an EMPTY
 * group still reports 'no-rig' rather than a false 'ok' (which would mean a silently inert
 * camera, exactly the failure this function exists to catch). Also scans
 * `eventGraphState.functions` bodies, closing a pre-existing blind spot: a rig node buried in
 * a function body executes fine at runtime but was invisible to this scan. This runs on a
 * 500ms poll (CameraRigNotice), not per frame, so the extra loops are fine here even though
 * the same pattern would be forbidden on a hot path.
 */
export function cameraRigState(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 'no-graph'
  if (nodes.some(n => RIG_NODE_TYPES.has(n.type))) return 'ok'
  for (const n of nodes) {
    if (n.type === 'graphGroup' && (n.data?.innerNodes ?? []).some(inner => RIG_NODE_TYPES.has(inner.type))) return 'ok'
  }
  for (const fn of Object.values(eventGraphState.functions ?? {})) {
    if ((fn.nodes ?? []).some(n => RIG_NODE_TYPES.has(n.type))) return 'ok'
  }
  return 'no-rig'
}
